import { useMemo } from 'react';

import { clx } from '@medusajs/ui';
import { buildCartFromOrder } from '@lib/util/build-cart-from-order';
import { getOrderNumber } from '@lib/util/get-order-number';
import { getPromotionsFromCart } from '@lib/util/get-promotions-from-cart';
import { fulfillmentStatusMap } from '@lib/util/order-status';
import CheckoutCart from '@modules/checkout/components/checkout-cart';
import Breadcrumbs from '@modules/common/components/breadcrumbs';
import Text from '@modules/common/components/text';
import { OrderLabel } from '@modules/common/components/order-label';
import { OrderStatusLabel } from '@modules/common/components/order-status-label';
import { PaymentStatusLabel } from '@modules/common/components/payment-status-label';
import { OrderCancelContainer } from '@modules/order/components/order-cancel';
import { OrderInfo, ShippingInfo } from '@modules/order/components/order-details';
import type { OrderDetailData } from '@modules/order/components/order-details/types';
import { OrderPaymentNotificationContainer } from '@modules/order/components/order-payment-notification';
import type { EnhancedStoreOrder } from '@modules/common/components/order-status-label';

interface OrderDetailsTemplateProps {
  order: OrderDetailData;
  isPerOrderSettlement: boolean;
}

const OrderLabelFulfillmentStatusTransform = (status: string) => {
  const config = fulfillmentStatusMap[status];
  if (!config) return '-';
  const { style, description } = config;
  return <OrderLabel kind={style}>{description}</OrderLabel>;
};

const OrderDetailsTemplate = ({
  order,
  isPerOrderSettlement,
}: OrderDetailsTemplateProps) => {
  const orderNumber = getOrderNumber(order);
  const cart = useMemo(() => buildCartFromOrder(order), [order]);
  const promotions = getPromotionsFromCart(order.cart);

  const breadcrumbsItems = useMemo(
    () => [
      {
        name: '訂單列表',
        href: '/account/orders',
      },
      {
        name: `訂單單號 ${orderNumber}`,
      },
    ],
    [orderNumber]
  );

  return (
    <div className="flex flex-col justify-center gap-y-4">
      <Breadcrumbs items={breadcrumbsItems} className="mb-4" />
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <Text as="h1" variant="2xl-semi" data-testid="order-detail-number">訂單單號 #{orderNumber}</Text>
          <div className="ml-auto flex items-center gap-2">
            <OrderPaymentNotificationContainer
              orderId={order.id}
              orderNumber={orderNumber}
              marxPayment={order.marxPayment}
              isPerOrderSettlement={isPerOrderSettlement}
            />
            <OrderCancelContainer order={order} orderNumber={orderNumber} />
          </div>
        </div>
        <div className="flex items-center gap-3" data-testid="order-detail-status">
          <div className="text-black-10">訂單狀態</div>
          <OrderStatusLabel order={order as EnhancedStoreOrder} />
        </div>
        <div className="flex items-center gap-3" data-testid="order-detail-fulfillment-status">
          <div className="text-black-10">出貨狀態</div>
          {order.fulfillment_status
            ? OrderLabelFulfillmentStatusTransform(order.fulfillment_status)
            : '-'}
        </div>
        <div className="flex items-center gap-3" data-testid="order-detail-payment-status">
          <div className="text-black-10">付款狀態</div>
          <PaymentStatusLabel order={order as EnhancedStoreOrder} />
        </div>
      </div>
      <div
        className="flex h-full w-full flex-col gap-4 bg-white"
        data-testid="order-details-container"
      >
        <div className="border-white-40 rounded-md border px-3 py-4">
          <CheckoutCart
            cart={cart}
            promotions={promotions}
            showImg={false}
            showIdx={true}
            orderSummaryClassName={clx('w-full', {
              'border-t border-white-40': promotions.length !== 0,
            })}
          />
        </div>
        <div className="flex gap-6">
          <OrderInfo order={order} />
          <ShippingInfo order={order} />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsTemplate;
